// src/contexts/DashboardContext.tsx

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  DashboardState,
  DashboardAction,
  DashboardUpdatePayload,
} from '@typeDefs/domain/dashboard';
import { dashboardService } from '@services/api/dashboardService';
import { dashboardMapper } from '@services/mappers/dashboardMapper';
import { useBusiness } from '@contexts/business/BusinessContext';

/**
 * Dashboard context for managing global dashboard state
 * Follows the same pattern as existing contexts in the codebase
 */

const STALE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Dashboard reducer to manage state transitions
 */
const dashboardReducer = (state: DashboardState, action: DashboardAction): DashboardState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
        lastFetched: new Date(),
        isStale: false,
      };

    case 'FETCH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
        isStale: true,
      };

    case 'UPDATE_DATA': {
      if (!state.data) return state;

      const updatedData = dashboardMapper.applyUpdate(state.data, action.payload);
      return {
        ...state,
        data: updatedData,
        lastFetched: new Date(),
      };
    }

    case 'MARK_STALE':
      return {
        ...state,
        isStale: true,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'INVALIDATE_DATA':
      return {
        ...state,
        isStale: true,
        lastFetched: null,
      };

    default:
      return state;
  }
};

/**
 * Initial dashboard state
 */
const initialState: DashboardState = {
  data: null,
  loading: false,
  error: null,
  lastFetched: null,
  isStale: false,
};

/**
 * Dashboard context type definition
 */
interface DashboardContextType {
  state: DashboardState;
  fetchDashboardData: (corporateEntityId: string, force?: boolean) => Promise<void>;
  updateDashboardData: (update: DashboardUpdatePayload) => void;
  markDataStale: () => void;
  clearError: () => void;
  refreshIfStale: (corporateEntityId: string) => Promise<void>;
  invalidateData: () => void;
}

/**
 * Dashboard context instance
 */
const DashboardContext = createContext<DashboardContextType | null>(null);

/**
 * Dashboard provider component
 */
export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const { businessProfile, businessDataRefreshTrigger } = useBusiness();

  /**
   * Fetch dashboard data from API
   */
  const fetchDashboardData = useCallback(
    async (corporateEntityId: string, force = false) => {
      // Check if data is fresh and force is not requested
      if (
        !force &&
        state.data &&
        state.lastFetched &&
        Date.now() - state.lastFetched.getTime() < STALE_TIME &&
        !state.isStale
      ) {
        console.log('Dashboard data is still fresh, skipping fetch');
        return;
      }

      if (!corporateEntityId) {
        console.warn('Cannot fetch dashboard data: corporateEntityId is required');
        return;
      }

      dispatch({ type: 'FETCH_START' });

      try {
        console.log(`Fetching dashboard data for corporate entity: ${corporateEntityId}`);

        const response = await dashboardService.getClientBalanceDetail(corporateEntityId);
        const dashboardData = dashboardMapper.fromProjectionsResponse(response);

        console.log('Dashboard data fetched successfully:', {
          accountBalance: dashboardData.accountBalance,
          totalAssets: dashboardData.totalAssets,
          pendingPayments: dashboardData.pendingPayments,
          lifetimeEarnings: dashboardData.lifetimeEarnings,
        });

        dispatch({ type: 'FETCH_SUCCESS', payload: dashboardData });
      } catch (error: unknown) {
        console.error(
          'Failed to fetch dashboard data:',
          error instanceof Error ? error.message : error,
        );
        dispatch({
          type: 'FETCH_ERROR',
          payload: error instanceof Error ? error.message : 'Failed to fetch dashboard data',
        });
      }
    },
    [state.data, state.lastFetched, state.isStale],
  );

  /**
   * Update dashboard data based on user actions
   */
  const updateDashboardData = useCallback((update: DashboardUpdatePayload) => {
    console.log('Updating dashboard data:', update);
    dispatch({ type: 'UPDATE_DATA', payload: update });
  }, []);

  /**
   * Mark data as stale (will trigger refresh on next access)
   */
  const markDataStale = useCallback(() => {
    console.log('Marking dashboard data as stale');
    dispatch({ type: 'MARK_STALE' });
  }, []);

  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Refresh data if it's marked as stale
   */
  const refreshIfStale = useCallback(
    async (corporateEntityId: string) => {
      if (state.isStale || !state.data) {
        console.log('Data is stale, refreshing...');
        await fetchDashboardData(corporateEntityId, true);
      }
    },
    [state.isStale, state.data, fetchDashboardData],
  );

  /**
   * Invalidate current data (forces refresh on next fetch)
   */
  const invalidateData = useCallback(() => {
    console.log('Invalidating dashboard data');
    dispatch({ type: 'INVALIDATE_DATA' });
  }, []);

  /**
   * Auto-mark data as stale after timeout
   */
  useEffect(() => {
    if (state.lastFetched && !state.isStale) {
      const timeoutId = setTimeout(() => {
        dispatch({ type: 'MARK_STALE' });
      }, STALE_TIME);

      return () => clearTimeout(timeoutId);
    }
  }, [state.lastFetched, state.isStale]);

  /**
   * Listen to business data refresh trigger and auto-refresh dashboard
   */
  useEffect(() => {
    // Skip initial render (when trigger is 0)
    if (businessDataRefreshTrigger > 0 && businessProfile?.id) {
      console.log('Business data refreshed, triggering dashboard refresh');
      fetchDashboardData(businessProfile.id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDataRefreshTrigger, businessProfile?.id]);

  const contextValue: DashboardContextType = {
    state,
    fetchDashboardData,
    updateDashboardData,
    markDataStale,
    clearError,
    refreshIfStale,
    invalidateData,
  };

  return <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>;
};

/**
 * Hook to use dashboard context
 */
export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

/**
 * Hook to get chart data from dashboard context
 */
export const useDashboardChart = () => {
  const { state } = useDashboard();

  if (!state.data) {
    return null;
  }

  return dashboardMapper.calculateChartSegments(state.data);
};
